import React, { useRef, useEffect } from 'react';
import { FacetBlock } from '@/features/shared/components/Facet';
import { arr } from '@/features/shared/lib/utils';

export default function ResearchersFilterSidebar({
  facet,
  filters,
  setFilters,
  pendingFilters,
  setPendingFilters,
  conjunctions,
  setConjunctions,
  sort,
  query,
  setQuery,
  pendingQuery,
  setPendingQuery,
  buildExtraParams,
  syncUrl,
  fetchList,
  cleanFilters,
  setPopup,
  setGraphExpanded,
}) {
  // Debounce timer for search input
  const typingTimerRef = useRef(null);
  useEffect(() => () => { if (typingTimerRef.current) clearTimeout(typingTimerRef.current); }, []);
  const DEBOUNCE_MS = 500;
  const runSearchNow = () => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    const fn = typeof fetchList === 'function' ? doSearch : doSearch; // keep same doSearch
    if (typeof fn === 'function') fn();
  };
  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    if (typeof setPendingQuery === 'function') setPendingQuery(val);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      runSearchNow();
    }, DEBOUNCE_MS);
  };
  const doSearch = () => {
    const q = (pendingQuery || '').trim();
    setQuery(q);
    // 그래프 닫기
    if (setGraphExpanded) setGraphExpanded(false);
    const extra = buildExtraParams();
    if (q) extra.sub_ks = q;
    // conjunction 파라미터 추가
    Object.entries(conjunctions || {}).forEach(([k, conj]) => {
      if (conj === 'and') {
        extra[`${k}_conjunction`] = 'and';
      }
    });
    syncUrl({ page: 1, query: q, filters, sort, conjunctions });
    try {
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    } catch (e) {
    }
    fetchList(1, { extra });
  };

  return (
    <div className="lg:w-[310px] lg:mt-[45px]">
      <div className="filter fixed top-[100%] right-0 z-[101] w-full h-full transition-top duration-300 px-6 py-[30px] bg-[#fff] overflow-y-auto lg:rounded-xl lg:static">
        <div className="filter-top flex items-center gap-[10px] relative lg:justify-between">
          <h3 className="text-[18px] font-bold">Researcher Search</h3>
          <button
            className="btn-resetAll text-[13px] text-[#6C7687] font-semibold cursor-pointer"
            onClick={() => {
              // reset query and filters
              setPendingQuery('');
              setFilters({});
              setPendingFilters({});
              setConjunctions({});
              const extra = {};
              if (sort?.order_by) extra.order_by = sort.order_by;
              if (sort?.order) extra.order = sort.order;
              syncUrl({ page: 1, filters: {}, sort, query: '', conjunctions: {} });
              try {
                if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                  window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }
              } catch (e) {
              }
              fetchList(1, { extra });
            }}
          >
            Reset all
          </button>
          <button className="filter-close absolute top-0 right-0 w-[25px] h-[25px] cursor-pointer lg:hidden">
            <img
              src={`${rmVars.themeUrl}images/clear-icon.png`}
              className="w-[18px]"
              alt="close"
            />
          </button>
        </div>

        <div className="filter-search relative mt-[20px]">
          <input
            type="text"
            placeholder="Search within result..."
            value={pendingQuery}
            onChange={handleSearchInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                runSearchNow();
              }
            }}
            className="w-full h-[46px] px-4 pr-[46px] border border-[#E4E8F1] outline-none rounded-lg text-[13px] text-[#333] placeholder:text-[#B4BCCC] hover:border-[#8D9AAF] focus:border-[#8D9AAF] disabled:border-[#E4E8F1] disabled:bg-[#F3F5F9] disabled:text-[#B4BCCC] disabled:cursor-not-allowed"
          />
          <button
            type="button"
            onClick={runSearchNow}
            className="absolute top-1/2 right-[18px] -translate-y-1/2 cursor-pointer"
          >
            <img
              src={`${rmVars.themeUrl}images/search-ico-8D9AAF.png`}
              className="w-[18px]"
              alt="search icon"
            />
          </button>
        </div>

        <div className="filter-cont mt-10">
          <div className="filter-item-wrap">
            {Object.entries(facet)
              .sort((a, b) => (a[0] === 'registered_db_name' ? -1 : (b[0] === 'registered_db_name' ? 1 : 0)))
              .map(([key, value]) => {
                const items = value?.result ? value : arr(value);
                if (Array.isArray(items) && !items.length) return null;
                if (items && typeof items === 'object' && !Array.isArray(items) && Array.isArray(items.result) && !items.result.length) return null;

                // key를 사람이 읽기 좋은 형태로 변환
                const title = value?.title || key
                  .split('_')
                  .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                  .join(' ');

                // 특정 facet만 옵션 활성화
                const isRegisteredDb = key === 'registered_db_name';

                // conjunction 필드 확인 (dept 등)
                const hasConjunction = value?.is_conjunction;
                const currentConjunction = hasConjunction ? (conjunctions[key] || null) : null;

                return (
                  <FacetBlock
                    key={key}
                    title={title}
                    items={items}
                    showAll={isRegisteredDb}
                    andQuery={isRegisteredDb}
                    facetKey={key}
                    selected={pendingFilters[key] || []}
                    conjunction={hasConjunction ? 'and' : null}
                    conjunctionValue={currentConjunction}
                    onConjunctionChange={(facetKey, conjunctionValue) => {
                      setConjunctions(prev => ({
                        ...prev,
                        [facetKey]: conjunctionValue
                      }));
                    }}
                    onToggle={(facetKey, value) => {
                      setPendingFilters(prev => {
                        const prevArr = Array.isArray(prev[facetKey]) ? prev[facetKey] : [];
                        const exists = prevArr.includes(value);
                        const nextArr = exists ? prevArr.filter(v => v !== value) : [...prevArr, value];
                        return { ...prev, [facetKey]: nextArr };
                      });
                    }}
                    onRefine={() => {
                      const next = cleanFilters(pendingFilters || {});
                      setFilters(next);
                      setPendingFilters(next);
                      // 그래프 닫기
                      if (setGraphExpanded) setGraphExpanded(false);
                      const extra = {};
                      Object.entries(next).forEach(([k, vals]) => {
                        if (Array.isArray(vals) && vals.length) extra[`${k}[]`] = vals;
                      });
                      // conjunction 파라미터 추가
                      Object.entries(conjunctions || {}).forEach(([k, conj]) => {
                        if (conj === 'and') {
                          extra[`${k}_conjunction`] = 'and';
                        }
                      });
                      if (query && String(query).trim() !== '') extra.sub_ks = String(query).trim();
                      if (sort?.order_by) extra.order_by = sort.order_by;
                      if (sort?.order) extra.order = sort.order;
                      syncUrl({ page: 1, filters: next, sort, query, conjunctions });
                      try {
                        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                        }
                      } catch (e) {
                      }
                      fetchList(1, { extra });
                    }}
                    onMore={(params) => {
                      // FacetBlock은 facetKey로 키를 넘김 → Popup은 key를 사용함
                      setPopup({ open: true, key: params?.facetKey || params?.key || '', ...params });
                    }}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
